import { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import type { Attribute } from "../../../types";
import { validateField } from "../../../utils/validation-rules";
import type {
  FileFieldConstraints,
  FileInfo,
  UploadedFormDataFileInfo,
} from "./types";

export const useFileValidation = (field: Attribute) => {
  return useCallback(
    (files: (File | UploadedFormDataFileInfo)[]): string | null => {
      const result = validateField(field, files);
      return result.isValid ? null : result.errorMessage;
    },
    [field],
  );
};

export const useFileState = () => {
  const [fileInfos, setFileInfos] = useState<readonly FileInfo[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const onChangeRef = useRef<
    ((files: (File | UploadedFormDataFileInfo)[] | undefined) => void) | null
  >(null);

  return {
    fileInfos,
    setFileInfos,
    isInitialized,
    setIsInitialized,
    uploadError,
    setUploadError,
    onChangeRef,
  };
};

export const useFileOperations = (
  setFileInfos: React.Dispatch<React.SetStateAction<readonly FileInfo[]>>,
  notifyFormChange: (files: readonly FileInfo[]) => void,
) => {
  const extractLocalFiles = useCallback(
    (files: readonly FileInfo[]): File[] => {
      return files
        .filter(
          (info): info is Extract<FileInfo, { type: "local" }> =>
            info.type === "local",
        )
        .map((info) => info.file);
    },
    [],
  );

  const removeFile = useCallback(
    (fileId: string): void => {
      setFileInfos((prev) => {
        const updated = prev.filter((info) => info.id !== fileId);

        const removedFile = prev.find((info) => info.id === fileId);
        if (removedFile?.type === "local" && removedFile.preview) {
          URL.revokeObjectURL(removedFile.preview);
        }

        setTimeout(() => notifyFormChange(updated), 0);
        return updated;
      });
    },
    [notifyFormChange, setFileInfos],
  );

  return {
    extractLocalFiles,
    removeFile,
  };
};

export const useFileDropzone = (
  constraints: FileFieldConstraints,
  disabled: boolean,
  readOnly: boolean,
  validateFiles: (files: (File | UploadedFormDataFileInfo)[]) => string | null,
  setUploadError: (error: string | undefined) => void,
  setFileInfos: React.Dispatch<React.SetStateAction<readonly FileInfo[]>>,
  notifyFormChange: (files: readonly FileInfo[]) => void,
) => {
  const handleFileDrop = useCallback(
    (acceptedFiles: File[]): void => {
      if (acceptedFiles.length === 0) return;

      const validationError = validateFiles(acceptedFiles);
      if (validationError) {
        setUploadError(validationError);
        return;
      }

      setUploadError(undefined);
      const newFileInfos: FileInfo[] = acceptedFiles.map((file, index) => ({
        type: "local" as const,
        file,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : undefined,
        id: `${file.name}-${file.size}-${index}`,
      }));

      setFileInfos((prev) => {
        const updatedFiles = constraints.multiple
          ? [...prev, ...newFileInfos].slice(0, constraints.maxFiles)
          : newFileInfos.slice(0, 1);

        setTimeout(() => notifyFormChange(updatedFiles), 0);
        return updatedFiles;
      });
    },
    [
      validateFiles,
      constraints,
      notifyFormChange,
      setUploadError,
      setFileInfos,
    ],
  );

  return useDropzone({
    multiple: constraints.multiple,
    maxFiles: constraints.maxFiles,
    disabled: disabled || readOnly,
    onDrop: handleFileDrop,
  });
};
